import {
  Badge,
  Button,
  Checkbox,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { IconPencil } from "@tabler/icons-react";
import { useState } from "react";
import { usePermission } from "@how/app-kit";
import {
  STANDINGS,
  STANDING_COLOR,
  STANDING_LABEL,
  useCompleteStep,
  usePathways,
  usePersonPathway,
  useSetStanding,
  type Standing,
} from "./membership-queries";

/** Where a person is on the pathway, on their record: the standing, and each step with a tick. */
export function PathwaySection({ personId }: { personId: string }) {
  const canManage = usePermission("membership.manage", { write: true });
  const pathway = usePersonPathway(personId);
  const complete = useCompleteStep();
  const [editing, setEditing] = useState(false);
  if (pathway.isError) return null;
  const manage = canManage.data === true;
  const v = pathway.data;
  return (
    <section aria-label="Pathway">
      <Group justify="space-between" align="center">
        <Title order={4}>Pathway</Title>
        {manage ? (
          <Button
            variant="subtle"
            size="compact-sm"
            leftSection={<IconPencil size={14} />}
            onClick={() => {
              setEditing(true);
            }}
          >
            {v?.standing ? "Change standing" : "Set standing"}
          </Button>
        ) : null}
      </Group>
      {v === undefined ? null : (
        <Stack gap="xs" mt="xs">
          {v.standing === null ? (
            <Text size="sm" c="dimmed">
              No standing yet.
            </Text>
          ) : (
            <Group gap="xs">
              <Badge variant="light" color={STANDING_COLOR[v.standing]}>
                {STANDING_LABEL[v.standing]}
              </Badge>
              <Text size="xs" c="dimmed">
                since{" "}
                {v.since === null
                  ? ""
                  : new Date(`${v.since}T00:00:00`).toLocaleDateString(undefined, {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                {v.note === null ? "" : ` · ${v.note}`}
              </Text>
            </Group>
          )}
          {v.pathway === null ? (
            <Text size="sm" c="dimmed">
              No pathway set up yet.
            </Text>
          ) : (
            <Stack gap={6}>
              <Text size="xs" c="dimmed">
                {v.pathway.name}
              </Text>
              {v.steps.map((s) => (
                <Checkbox
                  key={s.id}
                  size="sm"
                  label={s.name}
                  description={
                    s.completedOn === null
                      ? s.description
                      : `Done ${new Date(`${s.completedOn}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}${s.note === null ? "" : ` · ${s.note}`}`
                  }
                  checked={s.completedOn !== null}
                  disabled={!manage || complete.isPending}
                  onChange={(e) => {
                    complete.mutate({ personId, stepId: s.id, done: e.currentTarget.checked });
                  }}
                />
              ))}
            </Stack>
          )}
        </Stack>
      )}
      <StandingModal
        key={editing ? "open" : "closed"}
        opened={editing}
        personId={personId}
        current={v}
        onClose={() => {
          setEditing(false);
        }}
      />
    </section>
  );
}

function StandingModal({
  opened,
  personId,
  current,
  onClose,
}: {
  opened: boolean;
  personId: string;
  current:
    | {
        standing: Standing | null;
        since: string | null;
        note: string | null;
        pathway: { id: string } | null;
      }
    | undefined;
  onClose: () => void;
}) {
  const pathways = usePathways();
  const set = useSetStanding();
  const [standing, setStanding] = useState<Standing>(current?.standing ?? "attending");
  const [pathwayId, setPathwayId] = useState<string | null>(current?.pathway?.id ?? null);
  const [since, setSince] = useState(current?.since ?? new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState(current?.note ?? "");
  return (
    <Modal opened={opened} onClose={onClose} title="Standing" radius="lg">
      <Stack gap="sm">
        <Select
          label="Standing"
          data={STANDINGS.map((s) => ({ value: s, label: STANDING_LABEL[s] }))}
          value={standing}
          allowDeselect={false}
          onChange={(value) => {
            const s = STANDINGS.find((x) => x === value);
            if (s !== undefined) setStanding(s);
          }}
        />
        <Select
          label="Pathway"
          placeholder="None"
          clearable
          data={(pathways.data ?? [])
            .filter((w) => w.isActive)
            .map((w) => ({ value: w.id, label: w.name }))}
          value={pathwayId}
          onChange={setPathwayId}
        />
        <TextInput
          label="Since"
          type="date"
          value={since}
          onChange={(e) => {
            setSince(e.currentTarget.value);
          }}
        />
        <Textarea
          label="Note"
          placeholder="Why, or anything worth remembering"
          autosize
          minRows={2}
          maxLength={1000}
          value={note}
          onChange={(e) => {
            setNote(e.currentTarget.value);
          }}
        />
        {set.isError ? (
          <Text size="sm" c="red">
            The standing was not saved. Check your access and try again.
          </Text>
        ) : null}
        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={set.isPending}
            onClick={() => {
              set.mutate({ personId, standing, pathwayId, since, note }, { onSuccess: onClose });
            }}
          >
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
